import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Boxes, PackageCheck, Truck, BarChart3, ShieldCheck, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import DiyalogLogo from '../components/brand/DiyalogLogo';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const features = [
    { icon: PackageCheck, title: 'Sipariş & Toplama', desc: 'Tek / çoklu / wave toplama akışları tek panelde.' },
    { icon: Truck, title: 'Sevkiyat & Kargo', desc: 'Entegre kargo, etiket ve takip yönetimi.' },
    { icon: BarChart3, title: 'Canlı Operasyon Paneli', desc: 'Anlık KPI, throughput ve performans metrikleri.' },
  ];

  return (
    <div className="min-h-screen flex bg-[#F5F7FB]">
      {/* ── Sol marka paneli (lg+) ── */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-[42%] relative overflow-hidden flex-col justify-between p-12 text-white"
           style={{ background: 'linear-gradient(160deg,#16213E 0%,#0F1A3A 55%,#0B1330 100%)' }}>
        {/* dekoratif izgara/parıltılar */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full"
             style={{ background: 'radial-gradient(circle,#4263EB55,transparent 70%)' }} />
        <div className="pointer-events-none absolute bottom-0 -left-20 w-80 h-80 rounded-full"
             style={{ background: 'radial-gradient(circle,#7C93F933,transparent 70%)' }} />

        <DiyalogLogo size="lg" variant="dark" />

        <div className="relative z-10 max-w-md">
          <h1 className="text-3xl xl:text-4xl font-extrabold leading-tight tracking-tight">
            Fulfillment operasyonunuzun<br />tek merkezi.
          </h1>
          <p className="mt-4 text-brand-200/80 text-[15px] leading-relaxed">
            Mal kabulden sevkiyata kadar tüm depo akışını uçtan uca yönetin.
          </p>

          <div className="mt-10 space-y-5">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="shrink-0 w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-brand-200" />
                </div>
                <div>
                  <p className="font-semibold text-[15px]">{f.title}</p>
                  <p className="text-sm text-brand-200/70 leading-snug">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-xs text-brand-200/60">
          <ShieldCheck className="w-4 h-4" />
          <span>Güvenli oturum · Diyalog WMS · {new Date().getFullYear()}</span>
        </div>
      </div>

      {/* ── Sağ form paneli ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[420px]">
          {/* Mobil logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <DiyalogLogo size="md" variant="light" />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-ink tracking-tight">{t('auth.login') || 'Giriş Yap'}</h2>
            <p className="mt-1.5 text-sm text-slate-500">
              {t('auth.systemName') || 'Hesabınıza giriş yaparak operasyon panelini açın.'}
            </p>
          </div>

          {error && (
            <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                 data-testid="login-error">
              <AlertCircle className="w-[18px] h-[18px] mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
            {/* Fake inputs — password manager'ı kandır */}
            <input type="text" name="fakeuser" autoComplete="username" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
            <input type="password" name="fakepass" autoComplete="new-password" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />

            <div>
              <label htmlFor="wms-login-identifier" className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('auth.email') || 'E-posta'}
              </label>
              <input
                id="wms-login-identifier"
                name="wms-login-identifier"
                type="text"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder') || 'ornek@diyalog.com'}
                required
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-testid="email-input"
                className="w-full h-11 px-3.5 rounded-xl border border-slate-300 bg-white text-[15px] text-ink placeholder:text-slate-400
                           focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 transition"
              />
            </div>

            <div>
              <label htmlFor="wms-login-secret" className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('auth.password') || 'Şifre'}
              </label>
              <div className="relative">
                <input
                  id="wms-login-secret"
                  name="wms-login-secret"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  data-testid="password-input"
                  className="w-full h-11 px-3.5 pr-11 rounded-xl border border-slate-300 bg-white text-[15px] text-ink placeholder:text-slate-400
                             focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 transition"
                />
                <button type="button" onClick={() => setShowPass((v) => !v)} tabIndex={-1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        aria-label="şifreyi göster/gizle">
                  {showPass ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-button"
              className="w-full h-12 rounded-xl bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white font-semibold text-[15px]
                         shadow-lg shadow-brand-500/25 transition flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed
                         focus:outline-none focus:ring-4 focus:ring-brand-500/30"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> {t('auth.loggingIn') || 'Giriş yapılıyor…'}</>
              ) : (
                <><Boxes className="w-5 h-5" /> {t('auth.login') || 'Giriş Yap'}</>
              )}
            </button>
          </form>

          {process.env.NODE_ENV === 'development' && (
            <div className="mt-7 p-4 bg-brand-50 border border-brand-100 rounded-xl">
              <p className="text-xs text-brand-700 font-semibold mb-2">{t('auth.demoAccounts') || 'Demo hesaplar'}:</p>
              <p className="text-xs text-slate-600 font-mono">admin@example.com / admin123</p>
              <p className="text-xs text-slate-600 font-mono">operator@example.com / operator123</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
